/**
 * CampaignFilter - Filtro global de campanhas no Topbar.
 *
 * Botão compacto que abre popover com busca + lista de checkboxes.
 * Estado visual:
 *   - "Todas as campanhas" → botão outlined (estado neutro)
 *   - N campanhas selecionadas → botão preto (filtro ativo, padrão monocromático)
 */

import { useEffect, useRef, useState, useMemo } from 'react'
import Icon from './icons.jsx'
import { useCampaignFilter } from '../hooks/useCampaignFilter.js'

// Textos default — wording de campanha (Dashboard). Sobrescritos via prop
// `texts` quando o filtro é reutilizado para outro nível (grupos/anúncios).
const DEFAULT_TEXTS = {
  allLabel: 'Todas as campanhas',
  nounPlural: 'campanhas',
  placeholder: 'Buscar campanha...',
  empty: 'Nenhuma campanha encontrada',
  selectAll: 'Selecionar todas',
}

/**
 * @param {object} [props.controller] - Controlador opcional com a mesma shape
 *   do retorno de useCampaignFilter(). Quando fornecido, o componente opera em
 *   modo controlado (usado na página Google Ads, filtro dinâmico por aba). Sem
 *   ele, cai no hook global (Dashboard) — comportamento inalterado.
 * @param {object} [props.texts] - Overrides de wording (allLabel, nounPlural,
 *   placeholder, empty, selectAll).
 */
export default function CampaignFilter({ controller, texts } = {}) {
  // Hook global sempre chamado (regras de hooks); ignorado em modo controlado.
  const globalFilter = useCampaignFilter()
  const { available, selectedIds, isAllSelected, isFiltered, count, total, label, toggle, selectAll, clearAll } = controller || globalFilter
  const T = { ...DEFAULT_TEXTS, ...texts }
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const popoverRef = useRef(null)
  const buttonRef = useRef(null)

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return
      if (buttonRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  // Filtragem por busca (case-insensitive, em nome OU source)
  const filtered = useMemo(() => {
    if (!query.trim()) return available
    const q = query.toLowerCase().trim()
    return available.filter(
      (c) => c.name.toLowerCase().includes(q) || c.source.toLowerCase().includes(q)
    )
  }, [available, query])

  return (
    <div className="cf-wrap">
      <button
        ref={buttonRef}
        type="button"
        className={`cf-trigger ${isFiltered ? 'cf-trigger-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={isFiltered ? `${count} de ${total} ${T.nounPlural}` : T.allLabel}
      >
        <Icon n="filter" s={13} />
        <span className="cf-trigger-label">{label}</span>
        {/* Badge num\u00e9rico - vis\u00edvel apenas em telas pequenas quando filtrado */}
        {isFiltered && <span className="cf-trigger-badge">{count}</span>}
        <Icon n="chevron_d" s={12} />
      </button>

      {open && (
        <div ref={popoverRef} className="cf-popover" role="dialog" aria-label="Filtrar campanhas">
          {/* Busca */}
          <div className="cf-search">
            <Icon n="search" s={13} />
            <input
              type="text"
              className="cf-search-input"
              placeholder={T.placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {query && (
              <button className="cf-search-clear" onClick={() => setQuery('')} type="button" aria-label="Limpar busca">
                <Icon n="x" s={12} />
              </button>
            )}
          </div>

          {/* Selecionar todas */}
          {!query && (
            <button
              type="button"
              className="cf-row cf-row-meta"
              onClick={isAllSelected ? clearAll : selectAll}
            >
              <span className={`cf-check ${isAllSelected ? 'cf-check-on' : ''}`}>
                {isAllSelected && <Icon n="check" s={11} />}
              </span>
              <span className="cf-row-name">{T.selectAll}</span>
              <span className="cf-row-meta-count">{total}</span>
            </button>
          )}

          {/* Lista */}
          <div className="cf-list">
            {filtered.length === 0 ? (
              <div className="cf-empty">{T.empty}</div>
            ) : (
              filtered.map((c) => {
                const checked = selectedIds.includes(c.id)
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="cf-row"
                    onClick={() => toggle(c.id)}
                  >
                    <span className={`cf-check ${checked ? 'cf-check-on' : ''}`}>
                      {checked && <Icon n="check" s={11} />}
                    </span>
                    <span className="cf-row-name">{c.name}</span>
                    <span className="cf-row-source">{c.source}</span>
                    {c.status === 'paused' && <span className="cf-row-status">pausada</span>}
                  </button>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className="cf-footer">
            <span className="cf-count">
              {count} de {total} {count === 1 ? 'selecionada' : 'selecionadas'}
            </span>
            <div className="cf-actions">
              {isFiltered && (
                <button className="cf-btn-text" onClick={selectAll} type="button">
                  Resetar
                </button>
              )}
              <button className="cf-btn-primary" onClick={() => setOpen(false)} type="button">
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
